// i18n for the multilingual Indian market (WA4). Two layers:
//   1. A module-level API — t("ns.key", "English fallback"), getLocale/setLocale — usable anywhere,
//      including outside React (utils, api interceptors). Until a locale dict has a key, the English
//      fallback (or the key) is returned, so call sites migrate incrementally without breaking.
//   2. A React layer — <I18nProvider> + useI18n() — so changing the language re-renders the tree.
//      The two are kept in sync; the provider persists the choice to localStorage.
import React, { createContext, useContext, useState, useCallback, useMemo } from "react";

export const LOCALES = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी" },
  { code: "or", label: "ଓଡ଼ିଆ" },
];

// Seed dictionary — common nav + actions, enough to prove the wiring end-to-end. `en` is the source of
// truth; anything missing from hi/or transparently falls back to en, then to the inline fallback.
const DICTS = {
  en: {
    "nav.dashboard": "Dashboard", "nav.students": "Students", "nav.admissions": "Admissions",
    "nav.attendance": "Attendance", "nav.fees": "Fees", "nav.settings": "Settings",
    "nav.communication": "Communication", "nav.reports": "Reports", "nav.compliance": "Compliance",
    "action.save": "Save", "action.cancel": "Cancel", "action.delete": "Delete", "action.edit": "Edit",
    "action.add": "Add", "action.search": "Search", "action.close": "Close", "action.confirm": "Confirm",
    "common.loading": "Loading…", "common.noData": "No data", "common.language": "Language",
    "common.logout": "Log out", "common.profile": "Profile",
  },
  hi: {
    "nav.dashboard": "डैशबोर्ड", "nav.students": "विद्यार्थी", "nav.admissions": "प्रवेश",
    "nav.attendance": "उपस्थिति", "nav.fees": "शुल्क", "nav.settings": "सेटिंग्स",
    "nav.communication": "संचार", "nav.reports": "रिपोर्ट", "nav.compliance": "अनुपालन",
    "action.save": "सहेजें", "action.cancel": "रद्द करें", "action.delete": "हटाएँ", "action.edit": "संपादित करें",
    "action.add": "जोड़ें", "action.search": "खोजें", "action.close": "बंद करें", "action.confirm": "पुष्टि करें",
    "common.loading": "लोड हो रहा है…", "common.noData": "कोई डेटा नहीं", "common.language": "भाषा",
    "common.logout": "लॉग आउट", "common.profile": "प्रोफ़ाइल",
  },
  or: {
    "nav.dashboard": "ଡ୍ୟାସବୋର୍ଡ", "nav.students": "ଛାତ୍ରଛାତ୍ରୀ", "nav.admissions": "ଆଡମିଶନ",
    "nav.attendance": "ଉପସ୍ଥିତି", "nav.fees": "ଫିସ୍", "nav.settings": "ସେଟିଂସ୍",
    "nav.communication": "ଯୋଗାଯୋଗ", "nav.reports": "ରିପୋର୍ଟ", "nav.compliance": "ଅନୁପାଳନ",
    "action.save": "ସେଭ୍ କରନ୍ତୁ", "action.cancel": "ବାତିଲ୍", "action.delete": "ବିଲୋପ", "action.edit": "ସମ୍ପାଦନା",
    "action.add": "ଯୋଡ଼ନ୍ତୁ", "action.search": "ଖୋଜନ୍ତୁ", "action.close": "ବନ୍ଦ", "action.confirm": "ନିଶ୍ଚିତ",
    "common.loading": "ଲୋଡ୍ ହେଉଛି…", "common.noData": "କୌଣସି ତଥ୍ୟ ନାହିଁ", "common.language": "ଭାଷା",
    "common.logout": "ଲଗ୍ ଆଉଟ୍", "common.profile": "ପ୍ରୋଫାଇଲ୍",
  },
};

const STORAGE_KEY = "vp360.locale";

function _initialLocale() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && DICTS[saved]) return saved;
    const nav = (navigator.language || "en").slice(0, 2);
    if (DICTS[nav]) return nav;
  } catch { /* SSR / blocked storage */ }
  return "en";
}

let _locale = _initialLocale();

export function setLocale(locale) {
  if (DICTS[locale]) {
    _locale = locale;
    try { localStorage.setItem(STORAGE_KEY, locale); } catch { /* ignore */ }
  }
}

export function getLocale() { return _locale; }

export function availableLocales() { return LOCALES.map((l) => l.code); }

// Resolve a key for an explicit locale (used by the React layer so re-renders pick the new dict).
export function translate(locale, key, fallback) {
  const dict = DICTS[locale] || DICTS.en;
  if (key in dict) return dict[key];
  if (key in DICTS.en) return DICTS.en[key];
  return fallback !== undefined ? fallback : key;
}

export function t(key, fallback) { return translate(_locale, key, fallback); }

// ---------------- React layer ----------------
const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(_locale);
  const change = useCallback((next) => { setLocale(next); setLocaleState(getLocale()); }, []);
  const value = useMemo(() => ({
    locale,
    setLocale: change,
    locales: LOCALES,
    t: (key, fallback) => translate(locale, key, fallback),
  }), [locale, change]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  // Tolerate use outside a provider (e.g. isolated tests) — fall back to the module-level API.
  if (!ctx) return { locale: _locale, setLocale, locales: LOCALES, t };
  return ctx;
}

export default t;
